import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { type GetNodeCommentsResponse } from '@/types/comments';
import { createNodeComment } from '@/services/comment.service';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useCapabilities } from '@/hooks/useCapabilities';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useCreateNodeCommentMutation = () => {
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');
  const tErrors = useTranslations('errors');

  const { toast } = useToast();

  const queryClient = useQueryClient();
  const createNodeCommentWithAuth = useAuthenticatedQueryFn(createNodeComment);
  const shareConfig = useShareConfig();
  const { canCommentPrivately } = useCapabilities();

  const createNodeCommentMutation = useMutation({
    mutationFn: ({
      nodeId,
      body,
      parentId,
      private: isPrivate,
    }: {
      nodeId: string;
      body: string;
      parentId?: string;
      private?: boolean;
    }) =>
      createNodeCommentWithAuth({
        nodeId,
        body,
        parentId,
        // Never send private:true while the feature is flagged off, regardless of caller state.
        private: canCommentPrivately ? isPrivate : false,
      }),
    onSuccess: async (_response) => {
      // Everything is handled in the websocket events. However, keeping this code
      // as a backup for later. We will need to check if the reverb server is down and if it is apply
      // the following logic. We can move it to an exported function that we will use here and import in the websocket handle function.
      //
      //
      // // Get all query keys that match the node comment list pattern
      // const queryCache = queryClient.getQueryCache();
      // const queries = queryCache.findAll({ queryKey: [QueryKey.NodeCommentList, nodeId] });
      // queries.forEach((query) => {
      //   const queryKey = query.queryKey as [string, string, string, string];
      //   const sortOrder = queryKey[2]!;
      //   const sortValue = queryKey[3]!;
      //   // Add the new comment to the cache directly.
      //   queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(queryKey, (oldData) => {
      //     if (!oldData) {
      //       return oldData;
      //     }
      //     if (parentId) {
      //       return {
      //         ...oldData,
      //         pages: oldData.pages.map((page) => ({
      //           ...page,
      //           comments: page.comments.map((nodeComment) => {
      //             if (nodeComment.commentId !== parentId) {
      //               return nodeComment;
      //             }
      //             const latestRepliesWindowSize = Math.max(
      //               nodeComment.latestReplies.length,
      //               DEFAULT_VISIBLE_REPLIES_COUNT,
      //             );
      //             const nextLatestReplies = mergeRepliesById(nodeComment.latestReplies, [response]).slice(
      //               -latestRepliesWindowSize,
      //             );
      //             return {
      //               ...nodeComment,
      //               repliesCount: nodeComment.repliesCount + 1,
      //               latestReplies: nextLatestReplies,
      //             };
      //           }),
      //         })),
      //       };
      //     }
      //     // Transform the Comment response into a NodeComment structure.
      //     const newNodeComment = {
      //       commentId: response.id,
      //       nodeId,
      //       comment: response,
      //       latestReplies: [],
      //       repliesCount: 0,
      //       resolvedAt: null,
      //       resolvedBy: null,
      //     };
      //     // Determine if there's a next page based on pagination data
      //     const lastPage = oldData.pages[oldData.pages.length - 1];
      //     const lastPagination = lastPage?.pagination;
      //     const hasNextPage = lastPagination
      //       ? lastPagination.offset + lastPagination.limit < lastPagination.count
      //       : false;
      //     const insertPosition = shouldInsertNewComment({
      //       sortOrder: sortOrder,
      //       sortValue: sortValue,
      //       hasNextPage,
      //     });
      //     return insertCommentIntoCache({
      //       oldData,
      //       newNodeComment,
      //       insertPosition,
      //     });
      //   });
      // });
    },
    onError: (error: { message?: string; status?: number }, { nodeId, parentId }) => {
      // Invalidate in case the user tries to create a reply for a deleted parent comment
      const isMissingParentComment = Boolean(parentId && error.status === 404);

      if (isMissingParentComment) {
        const queryCache = queryClient.getQueryCache();
        const queries = queryCache.findAll({
          queryKey: [QueryKey.NodeCommentList, nodeId, shareConfig?.id],
          exact: false,
        });

        queries.forEach((query) => {
          queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
            if (!oldData) {
              return oldData;
            }

            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                comments: page.comments.filter((comment) => comment.commentId !== parentId),
                pagination: {
                  ...page.pagination,
                  count: Math.max(page.pagination.count - 1, 0),
                },
              })),
            };
          });
        });
      }

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: isMissingParentComment
          ? tToasts('createNodeCommentMutation.parentCommentDeleted')
          : (error.message ?? tErrors('toastDefaultDescription')),
      });
    },
  });

  return {
    createNodeCommentMutation,
  };
};
